import React, { useRef, useState } from "react";
import { UploadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadImage, mediaUrl, formatApiError } from "../lib/api";

// Upload control with preview. Calls onChange(url) with the stored image URL.
export const ImageField = ({ value, onChange, label, testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      {label && <p className="text-sm font-medium text-ink mb-2">{label}</p>}
      <div className="flex items-center gap-4">
        <div className="h-20 w-28 shrink-0 rounded-lg overflow-hidden border border-cream-2 bg-cream-2">
          {value ? (
            <img src={mediaUrl(value)} alt="preview" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full flex items-center justify-center text-ink-muted text-xs">No image</div>
          )}
        </div>
        <div className="flex-1">
          <button
            type="button"
            data-testid={testId ? `${testId}-upload` : "image-upload-btn"}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-lg bg-sage text-white px-4 py-2 text-sm font-semibold hover:bg-sage-dark transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {uploading ? "Uploading…" : "Upload / replace"}
          </button>
          <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
          <input
            type="text"
            data-testid={testId ? `${testId}-url` : "image-url-input"}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="or paste an image URL"
            className="mt-2 w-full rounded-lg border border-cream-2 px-3 py-2 text-sm text-ink-soft outline-none focus:border-sage"
          />
        </div>
      </div>
    </div>
  );
};

export default ImageField;
